// O estado completo do banco carregado em memória. O domínio opera sobre um
// Snapshot; o adaptador de persistência carrega e regrava (ADR-0001).
// Cada ticket que adiciona uma entidade estende o Snapshot e o adaptador.

import type { Id } from "./ids.ts";
import type { Etapa } from "./etapas.ts";
import type { Centavos } from "./money.ts";
import type { DataISO } from "./datas.ts";
import type { Modulo, NivelPermissao } from "./schema.ts";

export interface Perfil {
  id: Id;
  nome: string;
  ativo: boolean;
}

export interface Categoria {
  id: Id;
  nome: string;
  ativo: boolean;
}

/** Um preço e a data a partir da qual ele vale. */
export interface PrecoVigencia {
  valorCentavos: Centavos;
  desde: DataISO;
}

export interface TipoPeca {
  id: Id;
  nome: string;
  categoriaId: Id;
  /**
   * Rota da peça: os Processos por que ela passa, em ordem (ADR-0006). Valores
   * do vocabulário `listas.processosPeca`. Vazio até a tela de marcação (Fase 2).
   */
  processos: string[];
  ativo: boolean;
}

/** Preço base de um Tipo de Peça, a partir de uma data (ADR-0006: coleção própria). */
export interface PrecoBase {
  tipoId: Id;
  valorCentavos: Centavos;
  desde: DataISO;
}

/** Preço de um Tipo de Peça para um Cliente, a partir de uma data. */
export interface PrecoCliente {
  clienteId: Id;
  tipoId: Id;
  valorCentavos: Centavos;
  desde: DataISO;
}

export interface Usuario {
  id: Id;
  nome: string;
  login: string | null;
  /**
   * Hash da senha (bcrypt) — nunca a senha em si. `null` = login desativado
   * (ex.: usuário ainda não configurado). Calcular/conferir o hash é
   * responsabilidade de quem chama o domínio (`server/`), não do domínio —
   * aqui é só um texto opaco.
   */
  senhaHash: string | null;
  perfilId: Id | null;
  ativo: boolean;
}

export interface PermissaoModulo {
  perfilId: Id;
  modulo: Modulo;
  nivel: NivelPermissao;
}

export interface Funcionario {
  id: Id;
  nome: string;
  funcao: string;
  contato: string;
  admissao: DataISO | null;
  ativo: boolean;
}

export interface Insumo {
  id: Id;
  nome: string;
  categoria: string;
  unidade: string;
  /** Nível de alerta, sem tela própria — some do cadastro até o módulo de Estoque existir (ADR-0017). */
  estoqueMinimo: number;
  fornecedor: string | null;
  /** Fabricante/linha comercial, opcional. Junto com o Nome, forma a identidade do Insumo. */
  marca: string | null;
  ativo: boolean;
}

/** A que uma Categoria Financeira se aplica. */
export const APLICACOES_CATEGORIA = ["pagar", "receber", "ambos"] as const;
export type AplicacaoCategoria = (typeof APLICACOES_CATEGORIA)[number];

/**
 * Classificação de uma Conta a Pagar ou a Receber. Criável pelo usuário — nunca
 * fixa em código. `aplicaA` evita escolher "Energia elétrica" numa Conta a
 * Receber.
 */
export interface CategoriaFinanceira {
  id: Id;
  nome: string;
  subcategoria: string;
  aplicaA: AplicacaoCategoria;
  /**
   * Categoria de sistema não pode ser editada nem removida. Hoje só
   * "Faturamento", que a Fatura usa na Conta a Receber que gera (ADR-0011).
   */
  sistema: boolean;
  ativo: boolean;
}

/** Direção do dinheiro numa Conta. */
export const DIRECOES_CONTA = ["pagar", "receber"] as const;
export type DirecaoConta = (typeof DIRECOES_CONTA)[number];

/** De onde vem o dinheiro de uma Conta a Receber. */
export const FONTES_CONTA = ["cliente", "emprestimo", "transferencia", "outro"] as const;
export type FonteConta = (typeof FONTES_CONTA)[number];

/** Item de produto / insumo vinculado a uma Conta a Pagar. */
export interface ItemConta {
  insumoId: Id;
  unidade: string;
  quantidade: number;
  valorUnitarioCentavos: Centavos;
}

/**
 * Uma Conta a Pagar ou a Receber. As duas são o mesmo agregado, separadas pela
 * `direcao` (ADR-0011): compartilham descrição, valor, vencimento, Categoria
 * Financeira e a lista de Pagamentos.
 *
 * A Situação de Pagamento **não é campo** — deriva dos Pagamentos, como a
 * Situação do Lote deriva dos fatos (ADR-0005).
 */
export interface Conta {
  id: Id;
  direcao: DirecaoConta;
  descricao: string;
  /** Na Conta a Pagar, sempre avulso + itens (calculado na gravação, #87). */
  valorCentavos: number;
  /** Conta a Pagar: valor fora dos itens; `null` = só os itens. Sempre `null` na a Receber. */
  avulsoCentavos: number | null;
  vencimento: DataISO;
  categoriaId: Id;
  /** Só em Conta a Receber; `null` numa Conta a Pagar. */
  fonte: FonteConta | null;
  /** Quando a Fonte é um Cliente. */
  clienteId: Id | null;
  /**
   * A Fatura que gerou esta Conta a Receber (ADR-0011). `null` = Conta criada
   * à mão. Numa Conta de Fatura, valor e Fonte são travados na tela.
   */
  faturaId: Id | null;
  fornecedor: string | null;
  observacao: string;
  itens: ItemConta[];
}

/** Uma baixa numa Conta. Vários por Conta — é assim que pagamento parcial existe. */
export interface Pagamento {
  id: Id;
  contaId: Id;
  data: DataISO;
  valorCentavos: number;
}

/**
 * Quem retira dinheiro da empresa a título de pró-labore informal — sócio de
 * fato ou familiar. NÃO é Funcionário (não tem salário nem admissão) e NÃO é
 * Usuário (pode nunca acessar o sistema).
 *
 * Nome do termo provisório: se a família usa outra palavra, ela ganha.
 */
export interface Responsavel {
  id: Id;
  nome: string;
  /** Limite mensal de retirada. `null` = sem limite. Avisa, nunca bloqueia. */
  limiteMensalCentavos: number | null;
  ativo: boolean;
}

/**
 * Valor retirado por um Responsável. NÃO é uma Conta a Pagar (ADR-0011): soma
 * à parte das despesas da empresa em toda visão financeira, que é o motivo de
 * o módulo existir. Sem vencimento e sem Pagamento — retirada já é dinheiro
 * que saiu.
 */
export interface RetiradaPessoal {
  id: Id;
  responsavelId: Id;
  data: DataISO;
  valorCentavos: number;
}

export interface TempoReferencia {
  etapa: Etapa;
  /** Quantidade de peças de referência (> 0). */
  pecas: number;
  /** Tempo para processar `pecas` peças, em minutos (> 0). Canônico. */
  minutos: number;
}

export type BaseFaturamento = "entrada" | "saida";

export type CicloFechamento =
  | { tipo: "mensal" }
  | { tipo: "quinzenal" }
  | { tipo: "customizado"; diaInicio: number; diaFim: number };

export interface Cliente {
  id: Id;
  nome: string;
  endereco: string;
  contato: string;
  /** Três letras maiúsculas, único. Usado na numeração interna de Lotes. */
  codigo: string;
  tipo: string;
  baseFaturamento: BaseFaturamento;
  cicloFechamento: CicloFechamento;
  /** A devolução pode ser no mesmo dia da coleta? Padrão: não (ADR-0014). */
  devolucaoNoMesmoDia: boolean;
  /**
   * Dias do mês em que a Fatura vence, **na ordem dos períodos do Ciclo**:
   * um valor nos Ciclos mensal e customizado, dois no quinzenal (um por
   * quinzena). Lista vazia = não configurado (issue #40).
   */
  diasVencimento: number[];
  /**
   * Ids dos Tipos de Peça visíveis no lançamento de OS deste Cliente.
   * `null` = sem restrição, mostra todos os Tipos ativos. Editável no
   * cadastro do Cliente; a importação de OS por .csv ignora este filtro.
   */
  tiposPecaVisiveis: string[] | null;
  ativo: boolean;
}

export type AcaoParada = "coletar" | "devolver" | "ambos";

/**
 * Uma parada da rota de um Cliente: num conjunto de dias, num horário, o
 * motorista coleta o sujo, devolve o limpo, ou faz as duas coisas. O conjunto
 * de Paradas de um Cliente + `Cliente.devolucaoNoMesmoDia` é a "Rotina de
 * Coleta e Devolução" (ADR-0014).
 */
export interface Parada {
  id: Id;
  clienteId: Id;
  /** 0 = domingo … 6 = sábado. */
  diasSemana: number[];
  /** "HH:MM". */
  horario: string;
  acao: AcaoParada;
}

/**
 * Uma coleta: 1 Cliente + 1 data/hora. Nasce no lançamento e encerra numa
 * Devolução. A situação **não** é campo — é derivada (ADR-0005).
 */
export interface Lote {
  id: Id;
  clienteId: Id;
  /** Numeração interna `AAA-2026-0001`. Referência técnica. */
  codigo: string;
  /** Número do bloco de papel. Referência de negócio, **não é chave**. */
  os: number;
  coletaData: DataISO;
  /** "HH:MM". */
  coletaHora: string;
  /** Parada de coleta de origem; `null` = Coleta Avulsa. */
  paradaId: Id | null;
  /** Quantos Sacos vieram; `null` = não informado. */
  sacos: number | null;
  urgente: boolean;
  /** Prazo previsto informado à mão ("YYYY-MM-DD HH:MM"); sobrepõe o cálculo. */
  previstoManual: string | null;
  /**
   * OS de relavagem (ADR-0015): reprocesso de peças reclamadas pós-entrega.
   * Não entra no cálculo da Fatura — aparece na Planilha de Fechamento
   * marcada em cinza, fora de todos os totais.
   */
  relavagem: boolean;
  /** Marco da Devolução, que encerra o Lote. */
  devolvidoEm: DataISO | null;
  /** "HH:MM" da devolução; `null` em Lote não devolvido e em dado antigo. */
  devolvidoHora: string | null;
  canceladoEm: DataISO | null;
  motivoCancelamento: string | null;
  /**
   * Data em que a Fatura que continha esta OS foi reaberta (ADR-0024).
   * `null` enquanto a OS nunca esteve numa Fatura reaberta. Sobrevive a uma
   * Devolução regravada; deixa de valer assim que a OS entra numa Fatura
   * emitida de novo — `situacaoLote` checa "faturado" antes deste campo.
   */
  reabertoEm: DataISO | null;
  /**
   * Data do Fechamento de OS que foi reaberto ("Reabrir Fechamento" +
   * Salvar): enquanto a OS está reaberta para correção ela é Lançada
   * (`devolvidoEm` vazio — fora de Faturamento e Dashboard), mas as
   * contagens, retenções e baixas do fechamento continuam gravadas e a data
   * dele fica aqui para a Ficha reabrir com ela. Limpa no próximo "Fechar OS".
   */
  fechamentoAnteriorEm: DataISO | null;
  /** Hora ("HH:MM") do Fechamento reaberto — par de `fechamentoAnteriorEm`. */
  fechamentoAnteriorHora: string | null;
  /**
   * Quando o Fechamento desta OS foi registrado no sistema pela primeira vez
   * ("AAAA-MM-DDTHH:MM:SS", relógio do servidor). Desempata a ordem do
   * Backlog entre OS devolvidas na mesma data e hora (ADR-0028). Regravar o
   * Fechamento não muda; `null` em OS não fechada e em dado anterior.
   */
  fechamentoRegistradoEm: string | null;
  /**
   * O que foi lançado no Fechamento e guardado com "Salvar", sem fechar a OS
   * (Devolvida, Conferida e retidas com motivo, por Tipo). Não é Fechamento:
   * não entra no Backlog nem na Fatura — só volta para a Ficha. O "Fechar OS"
   * registra o Fechamento e apaga o rascunho. `null` sem rascunho.
   */
  rascunhoFechamento: RascunhoFechamento | null;
}

/** Linha do rascunho do Fechamento (ver `Lote.rascunhoFechamento`). */
export interface LinhaRascunhoFechamento {
  tipoId: Id;
  devolvida: number;
  /** Motivo pode estar vazio: ainda não escolhido. */
  retidas: { motivo: string; quantidade: number }[];
  conferida: number | null;
  conferidaMotivo: string | null;
}

export interface RascunhoFechamento {
  data: string;
  hora: string | null;
  linhas: LinhaRascunhoFechamento[];
}

/** Contagem por Tipo de Peça dentro de um Lote. */
export interface ContagemLote {
  loteId: Id;
  tipoId: Id;
  /** Quantidade declarada na comanda do hotel. Nunca muda por causa de uma
   * Conferida — é a prova do que o Cliente declarou (sobrevive do ADR-0020
   * pro ADR-0021). */
  recebida: number;
  /** Quantidade que voltou limpa **deste** Lote, apurada no Fechamento de OS. */
  devolvida: number;
  /** O que a lavanderia contou de fato, só para o cálculo do excedente/Backlog
   * — `null` quando não há divergência (vale a Recebida). Só existe dentro do
   * Fechamento de OS, nunca editável fora dele (ADR-0021). */
  conferida: number | null;
  /** Por que a Conferida difere da Recebida. `null` quando não há Conferida. */
  conferidaMotivo: string | null;
  /** Quantidade declarada como Retida no papel da OS (foto do momento, não cumulativa).
   * Preenchida no Lançar OS apenas. `null` se não foi preenchida. */
  retidaInformada: number | null;
}

/**
 * Uma Peça que ficou retida na Devolução de um Lote. O conjunto de retenções de
 * um Lote **é** o Sublote dele, código `AAA-2026-0001-R` (ADR-0007).
 */
export interface Retencao {
  /** Lote em cuja Devolução a Peça ficou — o dono do Sublote. */
  loteId: Id;
  tipoId: Id;
  motivo: string;
  quantidade: number;
}

/**
 * Baixa do Backlog: Peças de um Sublote que voltaram de carona na Devolução de
 * um Lote seguinte do mesmo Cliente (ADR-0007).
 */
/**
 * O que aconteceu com a Peça do Backlog que uma OS usou (ADR-0025):
 * `"devolvida"` voltou ao Cliente de carona (é faturada, ADR-0019);
 * `"retida"` foi reprocessada e ficou retida DE NOVO nesta OS — sai da linha
 * de origem e passa a contar no Sublote desta OS, sem ser faturada.
 */
export type DestinoBaixa = "devolvida" | "retida";

export interface BaixaBacklog {
  /** Lote da Devolução em que as Peças voltaram — a carona. */
  loteId: Id;
  /** Lote de origem: de qual Sublote saíram. */
  origemLoteId: Id;
  tipoId: Id;
  motivo: string;
  quantidade: number;
  destino: DestinoBaixa;
}

/**
 * Saldo de Backlog que já existia num Cliente antes do sistema entrar em
 * operação — sem Lote de origem por trás. Uma linha por (Cliente, Tipo);
 * cadastrar de novo substitui a quantidade anterior (ADR-0019).
 */
export interface BacklogInicialLinha {
  clienteId: Id;
  tipoId: Id;
  quantidade: number;
}

/**
 * Baixa do Backlog Inicial de carona numa Devolução — paralela a
 * `BaixaBacklog`, mas chaveada por Cliente em vez de Lote de origem (o
 * Backlog Inicial não tem Lote). É o único saldo que pode ficar negativo
 * (ADR-0019).
 */
export interface BaixaBacklogInicial {
  /** Lote da Devolução em que as Peças voltaram — a carona. */
  loteId: Id;
  clienteId: Id;
  tipoId: Id;
  quantidade: number;
  destino: DestinoBaixa;
}

/**
 * Saída definitiva de Peça do Backlog: extraviada ou danificada, não volta ao
 * Cliente (ADR-0022). Diferente de `BaixaBacklog`, que é devolução de carona —
 * ali a Peça voltou; aqui ela não volta mais.
 */
export interface Perda {
  id: Id;
  clienteId: Id;
  tipoId: Id;
  /** Sublote de origem; `null` = Backlog Inicial. */
  origemLoteId: Id | null;
  /** Motivo da retenção de origem — identifica a linha de saldo. */
  motivoOrigem: string;
  /** Por que saiu de vez — valor de `listas.motivosDivergencia`. */
  motivo: string;
  quantidade: number;
  data: DataISO;
}

/** Intervalo de datas [de, ate] inclusive — o Período de Faturamento. */
export interface Periodo {
  de: DataISO;
  ate: DataISO;
}

/** Ajuste manual de **valor** numa Fatura (descrição obrigatória, ADR #19). */
export interface AjusteFatura {
  id: Id;
  descricao: string;
  /** Centavos. Negativo = desconto. */
  valorCentavos: Centavos;
  /** Preso a um Tipo (aparece na linha dele) ou `null` = linha livre. */
  tipoId: Id | null;
}

/** Uma peça numa Compensação: quantidade com sinal (negativa tira). */
export interface PecaCompensada {
  tipoId: Id;
  quantidade: number;
}

/**
 * Compensação de Peças (ADR-0031): corrige as peças de uma OS já faturada —
 * ou anterior ao sistema — na fatura seguinte. Vira uma linha própria na
 * Planilha ("(Comp.) nº", com a data daquela OS), contando nos totais, pelo
 * preço da data da OS. Quantidade negativa tira peças cobradas a mais.
 */
export interface CompensacaoFatura {
  id: Id;
  /** A OS em que a falha aconteceu. */
  os: number;
  /** Data de referência daquela OS no faturamento (a do Lote, quando ele
   * está no sistema; a informada, quando é anterior ao sistema). */
  data: DataISO;
  /** O Lote da OS, quando está no sistema; `null` para OS anterior a ele. */
  loteId: Id | null;
  motivo: string;
  pecas: PecaCompensada[];
}

/** Linha de peça congelada na emissão — quantia e valor não mudam mais. */
export interface LinhaFaturaCongelada {
  tipoId: Id;
  quantidade: number;
  /** Quantas dessas vieram do Backlog (para o marcador da Planilha). */
  deRetidas: number;
  subtotalCentavos: Centavos;
  /**
   * Os preços unitários usados, em ordem de data (mais de um quando o preço
   * mudou no período ou há Compensação de Peças). Ausente nas faturas
   * emitidas antes deste campo existir — aí a tela mostra a média.
   */
  precosCentavos?: Centavos[];
}

/**
 * Fatura de um Cliente num Período (ticket #19). Rascunho enquanto `numero` é
 * nulo — recalcula sozinha; emitida quando ganha `numero` e `emitidaEm`, e aí os
 * valores ficam congelados. Reabrir volta ao rascunho, guardando a justificativa.
 */
export interface Fatura {
  id: Id;
  clienteId: Id;
  periodo: Periodo;
  /** `2026/0001`, sequência anual única. `null` = rascunho. */
  numero: string | null;
  emitidaEm: DataISO | null;
  ajustes: AjusteFatura[];
  /** Compensações de Peças (ADR-0031) — congeladas junto na emissão. */
  compensacoes: CompensacaoFatura[];
  /** Lotes cujas Devoluções compõem a Fatura — congelado na emissão. */
  loteIds: Id[];
  /** Linhas de peça congeladas — só nas emitidas. */
  linhasCongeladas: LinhaFaturaCongelada[];
  reaberturas: { em: DataISO; justificativa: string }[];
  /**
   * Vencimento da Fatura, gravado na emissão. Pré-preenchido pelo Dia de
   * Vencimento do Cliente e ajustável à mão (issue #40). `null` = não
   * informado.
   */
  vencimento: DataISO | null;
}

/**
 * Data de referência de um Lote no faturamento: `coleta` (padrão — o Lote é
 * faturado no período em que foi coletado, ao preço vigente na coleta) ou
 * `devolucao` (período e preço pela data em que voltou).
 */
export type DataFaturamento = "coleta" | "devolucao";

/** Parâmetros gerais do sistema (chave única). */
export interface Config {
  /** Rodapé da tabela de preços exportada. */
  rodapeExport: { slogan: string; whatsapp: string };
  /** Qual data do Lote o faturamento usa como referência. */
  dataFaturamento: DataFaturamento;
  /** Dashboard abre com os N meses inteiros anteriores + o corrente (ADR-0032). */
  mesesPadraoDashboard: number;
  /** Peças na base da taxa para um item disputar o ranking do Dashboard (ADR-0032). */
  volumeMinimoRanking: number;
}

/** Vocabulários que o usuário edita (função de Funcionário, categoria de Insumo…). */
export interface Listas {
  funcoesFuncionario: string[];
  categoriasInsumo: string[];
  unidadesInsumo: string[];
  tiposCliente: string[];
  /** Processos por que um Tipo de Peça pode passar (ADR-0006). */
  processosPeca: string[];
  /** Motivos de uma Peça ficar retida numa Devolução (ADR-0007). */
  motivosRetencao: string[];
  /** Por que uma Peça não voltou ao Cliente: alimenta a divergência do
   * Fechamento de OS e a Perda do Backlog (ADR-0021, ADR-0022). */
  motivosDivergencia: string[];
}

export interface Snapshot {
  perfis: Perfil[];
  usuarios: Usuario[];
  permissoesModulo: PermissaoModulo[];
  categorias: Categoria[];
  tiposPeca: TipoPeca[];
  precosBase: PrecoBase[];
  precosCliente: PrecoCliente[];
  clientes: Cliente[];
  paradas: Parada[];
  lotes: Lote[];
  contagens: ContagemLote[];
  retencoes: Retencao[];
  baixasBacklog: BaixaBacklog[];
  backlogInicial: BacklogInicialLinha[];
  baixasBacklogInicial: BaixaBacklogInicial[];
  perdas: Perda[];
  faturas: Fatura[];
  funcionarios: Funcionario[];
  insumos: Insumo[];
  categoriasFinanceiras: CategoriaFinanceira[];
  contas: Conta[];
  pagamentos: Pagamento[];
  responsaveis: Responsavel[];
  retiradasPessoais: RetiradaPessoal[];
  temposReferencia: TempoReferencia[];
  listas: Listas;
  config: Config;
}

export function emptySnapshot(): Snapshot {
  return {
    perfis: [],
    usuarios: [],
    permissoesModulo: [],
    categorias: [],
    tiposPeca: [],
    precosBase: [],
    precosCliente: [],
    clientes: [],
    paradas: [],
    lotes: [],
    contagens: [],
    retencoes: [],
    baixasBacklog: [],
    backlogInicial: [],
    baixasBacklogInicial: [],
    perdas: [],
    faturas: [],
    funcionarios: [],
    insumos: [],
    categoriasFinanceiras: [],
    contas: [],
    pagamentos: [],
    responsaveis: [],
    retiradasPessoais: [],
    temposReferencia: [],
    listas: {
      funcoesFuncionario: [],
      categoriasInsumo: [],
      unidadesInsumo: [],
      tiposCliente: [],
      processosPeca: [],
      motivosRetencao: [],
      motivosDivergencia: [],
    },
    config: {
      rodapeExport: {
        slogan:
          "Cuidamos da roupa de cama, banho e mesa, enquanto você cuida do hóspede.",
        whatsapp: "(41) 99616-5156",
      },
      dataFaturamento: "coleta",
      mesesPadraoDashboard: 3,
      volumeMinimoRanking: 50,
    },
  };
}
